import { styled, Box } from "@mui/material";
import { ReactElement } from "react";

import { ThreadMessage } from "~frontend/__generated__/graphql";
import { useCurrentUserIsStaff } from "~frontend/providers/LoggedInUserProvider";
import { palette } from "~frontend/theme";

import MessageInfoButton from "./MessageInfoButton";
import formatMessageTime from "./formatMessageTime";

const MessageWrapper = styled(Box)({
  backgroundColor: palette.white,
  borderWidth: "1px",
  borderStyle: "solid",
  borderColor: palette.midGray,
  color: palette.navy,
  maxWidth: "540px",
  marginBottom: "10px",
  borderRadius: "20px",
  borderBottomLeftRadius: 0,
  padding: "24px",

  "a, a:visited": {
    color: palette.navy,
  },
});

export default function IncomingMessage({
  message,
  onClickInfoButton,
  showCampaignContext,
}: {
  message: ThreadMessage;
  onClickInfoButton: () => void;
  showCampaignContext?: boolean;
}): ReactElement {
  const isStaff = useCurrentUserIsStaff();

  const hasAttachments = !!message.mediaAttachments?.find(attachment => {
    // Ignore SMIL attachments when determining if the message has attachments.
    // SMIL describes how to display attachments, and is included in all MMS
    // messages, even ones that don't have any real attachments.
    if (attachment.mimeType) {
      return attachment.mimeType !== "application/smil";
    } else {
      try {
        const url = new URL(attachment.url);
        return !url.pathname.toLowerCase().endsWith(".smil");
      } catch (e) {
        console.warn("Error parsing attachment URL", attachment.url, e);
        return true;
      }
    }
  });

  return (
    <MessageWrapper>
      <Box>
        <span
          style={{ whiteSpace: "pre-wrap", overflowWrap: "break-word" }}
          data-testid="thread-message-history-label"
          data-message-direction="inbound"
        >
          {message.text}
        </span>
        {hasAttachments
          ? " (this message has an MMS attachment that cannot currently be displayed)"
          : null}
      </Box>
      <Box sx={{ fontSize: "10px" }}>
        {formatMessageTime(message.threadOrderTime)}
        {isStaff && showCampaignContext && (
          <MessageInfoButton onClick={onClickInfoButton} />
        )}
      </Box>
    </MessageWrapper>
  );
}
